/*
 * Turning proposed changes into a walkthrough of the real console.
 *
 * The tour is DERIVED from the proposals, never authored by the model. Ada
 * already decided what to change and in what order; a walkthrough is the same
 * list pointed at the controls instead of applied over HTTP. That means a tour
 * can never describe a change the appliance was not actually asked for, and
 * there is no path from model output to a DOM selector.
 *
 * Controls are addressed by `data-tour` attributes on the panels themselves —
 * an explicit contract, greppable from both ends, rather than CSS selectors or
 * label matching that quietly rot when a panel is restyled. A step whose target
 * is missing still works: it falls back to the sidebar entry for its panel, so
 * an unmapped change guides the operator to the right screen instead of
 * nowhere. tourSteps.test.js asserts every target named here exists.
 */

import { changeTitle, describeChange } from "./proposalCopy";
import { navLabel } from "../navigation";

/* Concealment layers stack, and the appliance refuses a layer whose foundation
   is missing. So the order changes are made in is not cosmetic — get it wrong
   and every step but one is rejected.
 *
 * RANK is depth in that stack. Turning things ON climbs it, so the foundation
 * goes first; turning things OFF descends it, so the top comes off first. This
 * mirrors what the Obfuscation panel does when it moves between rungs, and it
 * is the reason Ada's proposals get re-ordered rather than replayed in the
 * order she happened to raise them. */
const RANK = {
  propose_wire_obfuscation: 0,
  propose_blending: 1,
  propose_chaff: 2,
  propose_chaff_shape: 2,
  propose_timing_jitter: 2,
  propose_constant_rate_shaping: 2,
  propose_constant_rate_shape: 2,
  propose_anchor_balance: 3,
  propose_anchor_weights: 3,
};

const disables = (p) => {
  const b = p.body || {};
  return b.enabled === false || b.mode === "off";
};

/**
 * orderChanges sorts proposals into the order they can actually be applied.
 *
 * Teardown before build-up, deepest layer removed first and laid down first —
 * the same sequence the panel itself uses. Anything outside the stack keeps its
 * original position, since the sort is stable.
 */
export function orderChanges(proposals) {
  return [...(proposals || [])].sort((a, b) => {
    const [da, db] = [disables(a), disables(b)];
    if (da !== db) return da ? -1 : 1; // remove layers before adding any
    const [ra, rb] = [RANK[a.tool] ?? 1, RANK[b.tool] ?? 1];
    return da ? rb - ra : ra - rb;
  });
}

/* Where each change lives. `target` is a data-tour id; `pick` chooses between
 * ids for a tool that drives more than one control.
 *
 * How to tell the change has LANDED is not here, deliberately. The appliance
 * states that with the proposal — it is the only place that knows both the
 * setting and the reading that reflects it — and this file only decides where
 * to point. A second copy of that mapping is what made a completed walkthrough
 * fail to register: the copy watched a field the tool did not have. */
const CONTROLS = {
  propose_wire_obfuscation: {
    panel: "obfuscation",
    title: (_p, target) => (target === "obf.pad" ? "Packet padding" : "Protocol concealment"),
    // One endpoint drives two switches, so the body alone does not say which
    // one the operator has to touch. The before-state does: whichever of the
    // two this change actually moves is the one to point at.
    pick: (body, current) => {
      const b = body || {};
      const c = current || {};
      if (b.enabled === true && c.obf_wire === true && b.pad !== c.obf_pad) return "obf.pad";
      return "obf.wire";
    },
  },
  propose_blending: {
    panel: "obfuscation", target: "obf.blend",
  },
  propose_chaff: {
    panel: "obfuscation", target: "obf.chaff",
  },
  propose_chaff_shape: { panel: "obfuscation", target: "obf.chaff" },
  propose_timing_jitter: {
    panel: "obfuscation", target: "obf.jitter",
  },
  propose_constant_rate_shaping: {
    panel: "obfuscation", target: "obf.strict",
  },
  propose_constant_rate_shape: { panel: "obfuscation", target: "obf.strict" },
  propose_anchor_balance: {
    panel: "obfuscation", target: "obf.anchors",
  },
  propose_anchor_weights: { panel: "obfuscation", target: "obf.anchors" },
  propose_rotate_anchors_now: { panel: "obfuscation", target: "obf.anchors" },
  propose_mtd_rotation: { panel: "obfuscation", target: "obf.rotation" },
  propose_rotate_now: { panel: "obfuscation", target: "obf.rotation" },

  propose_class_encryption: { panel: "encryption" },
  propose_encryption_all_classes: { panel: "encryption" },
  propose_class_action: { panel: "traffic" },
  propose_class_wan_order: { panel: "traffic" },
  propose_class_wan_priority: { panel: "wan-priority" },
  propose_apply_wan_priority: { panel: "wan-priority" },
  propose_reset_wan_priority: { panel: "wan-priority" },
  propose_qos_thresholds: { panel: "qos" },
  propose_failover_thresholds: { panel: "health" },
  propose_probing_config: { panel: "health" },
  propose_throughput_probing: { panel: "health" },
  propose_aqm_rates: { panel: "aqm" },
  propose_clear_aqm: { panel: "aqm" },
  propose_cloud_switch: { panel: "cloud" },
  propose_classifier_mode: { panel: "diag-devices" },
  propose_wan_suppression: { panel: "diag-overview" },
};

/**
 * buildTour returns one step per proposal, in the order they must be applied.
 *
 * Steps carry the proposal id so the overlay can confirm each one against live
 * state — the walkthrough watches the operator do it rather than assuming.
 */
export function buildTour(proposals) {
  return orderChanges(proposals).map((p, i) => {
    const control = CONTROLS[p.tool] || {};
    const target = control.pick ? control.pick(p.body, p.current) : control.target;
    const panel = control.panel || "dashboard";
    return {
      id: p.id,
      tool: p.tool,
      // Carried so a finished walkthrough can be verified against the settings
      // it was meant to produce, the same way an applied card is.
      body: p.body || {},
      panel,
      panelLabel: navLabel(panel) || "the console",
      target: target || null,
      title: control.title ? control.title(p, target) : changeTitle(p.tool),
      instruction: instructionFor(p, target),
      verifyPath: p.current_path || null,
      // Straight from the appliance. Absent means nothing to watch, and the
      // step waits to be told rather than advancing on a guess.
      verify: Array.isArray(p.verify) ? p.verify : null,
      // The assistant's sentence about this control, on this box, today.
      why: typeof p.why === "string" ? p.why.trim() : "",
      // A change to make, as opposed to a tour stop that is only explained.
      kind: "change",
      index: i,
    };
  });
}

/* What the operator should actually do at this step, in their words. A switch
   is "turn it on"; anything with values is "set it to X". */
function instructionFor(p, target) {
  const body = p.body || {};

  // One endpoint, two switches: describe only the one being pointed at, or the
  // note reads "turn this on, then set pad on" about a single toggle.
  if (p.tool === "propose_wire_obfuscation") {
    return (target === "obf.pad" ? body.pad : body.enabled) ? "Turn this on." : "Turn this off.";
  }

  const keys = Object.keys(body);

  if (keys.length === 1 && typeof body[keys[0]] === "boolean") {
    return body[keys[0]] ? "Turn this on." : "Turn this off.";
  }
  if (body.enabled === true && keys.length > 1) {
    return `Turn this on, then set ${describeChange(omit(body, "enabled"), p.tool)}.`;
  }
  if (body.enabled === false) return "Turn this off.";
  return `Set ${describeChange(body, p.tool)}.`;
}

const omit = (obj, key) => {
  const { [key]: _drop, ...rest } = obj;
  return rest;
};

/** readPath resolves a dotted field path against a reading. */
export function readPath(doc, path) {
  if (!doc || !path) return undefined;
  return path.split(".").reduce((v, k) => (v == null ? undefined : v[k]), doc);
}

/**
 * stepDone reports whether the appliance now shows what this step asked for.
 *
 * EVERY check must pass: a change that set two things is not done when one of
 * them landed. Compared as strings so "uniform" matches "uniform" and true
 * matches true without caring which side of the wire the JSON came from.
 */
export function stepDone(step, reading) {
  if (!step || !Array.isArray(step.verify) || !step.verify.length) return false;
  if (reading == null) return false;

  return step.verify.every((check) => {
    const got = readPath(reading, check.path);
    if (got === undefined) return false;
    return String(got) === String(check.want);
  });
}
